import { Link } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";

const CATEGORY_LABELS = {
  administratif: "Administratif",
  finance: "Finance",
  sante: "Santé",
  auto: "Auto & Transport",
  famille: "Famille",
  "vie-pratique": "Vie Pratique",
};

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1615749356515-6b07ae5d5378?w=800&q=80";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-MA", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function ArticleCard({ article, featured = false }) {
  const label = CATEGORY_LABELS[article.category] || article.category;

  return (
    <article
      data-testid={`article-card-${article.slug}`}
      className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-1 hover:border-blue-200 flex flex-col"
    >
      {/* Image */}
      <Link to={`/article/${article.slug}`} className="block overflow-hidden">
        <div className={`${featured ? "aspect-video" : "aspect-[16/9]"} bg-slate-100`}>
          <img
            src={article.featured_image || FALLBACK_IMAGE}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            loading="lazy"
            onError={(e) => {
              e.target.src = FALLBACK_IMAGE;
            }}
          />
        </div>
      </Link>

      {/* Body */}
      <div className="p-5 sm:p-6 flex flex-col flex-1">
        {/* Category badge */}
        <div className="mb-3">
          <Link
            to={`/categorie/${article.category}`}
            data-testid={`article-card-category-${article.slug}`}
            className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors"
          >
            {label}
          </Link>
        </div>

        {/* Title */}
        <h2
          className={`font-heading font-bold text-slate-900 mb-3 line-clamp-2 leading-snug ${
            featured ? "text-xl sm:text-2xl" : "text-lg"
          }`}
        >
          <Link
            to={`/article/${article.slug}`}
            data-testid={`article-card-title-${article.slug}`}
            className="hover:text-blue-600 transition-colors"
          >
            {article.title}
          </Link>
        </h2>

        {/* Excerpt */}
        {article.excerpt && (
          <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 flex-1 mb-4">
            {article.excerpt}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-auto">
          {article.createdAt && (
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formatDate(article.createdAt)}</span>
            </div>
          )}
          <Link
            to={`/article/${article.slug}`}
            data-testid={`article-card-read-${article.slug}`}
            aria-label={`Lire l'article : ${article.title}`}
            className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors ml-auto"
          >
            Lire <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}
